import classNames from 'classnames';
import React, { forwardRef } from 'react';

import { useButton } from '@react-aria/button';
import { FocusRing } from '@react-aria/focus';
import { useHover } from '@react-aria/interactions';
import { mergeProps } from '@react-aria/utils';

import type { ButtonProps } from '@react-types/button';
import type { DOMProps, StyleProps, ValidationState } from '@react-types/shared';

import { useFocusableRef } from '../../../utils';

interface IProps extends ButtonProps, DOMProps, StyleProps {
  focusRingClass?: string;
  isQuiet?: boolean;
  isActive?: boolean;
  validationState?: ValidationState;
}

export const NormalButton = forwardRef<HTMLButtonElement, IProps>((props, ref) => {
  const { isQuiet, isDisabled, validationState, children, autoFocus, isActive, focusRingClass } =
    props;

  const domRef = useFocusableRef(ref);
  const { buttonProps, isPressed } = useButton(props, domRef);
  const { hoverProps, isHovered } = useHover({ isDisabled });

  const focusRingClassname = React.useMemo(() => {
    return classNames('focus-ring-class', focusRingClass);
  }, [focusRingClass]);
  const buttonClassname = React.useMemo(() => {
    return classNames('button-class px-3', {
      'quiet-class': isQuiet,
      'is-active-class': isActive || isPressed,
      'is-disabled-class': isDisabled,
      'invalid-class': validationState === 'invalid',
      'is-hovered-class': isHovered
    });
  }, [isActive, isDisabled, isHovered, isPressed, isQuiet, validationState]);

  return (
    <FocusRing focusRingClass={focusRingClassname} autoFocus={autoFocus}>
      <button {...mergeProps(buttonProps, hoverProps)} ref={domRef} className={buttonClassname}>
        {children}
      </button>
    </FocusRing>
  );
});

NormalButton.displayName = 'NormalButton';
